import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Pentagon radar chart for visualizing bullet point scores
 * 
 * Shows 5 metrics on a 0-3 scale:
 * - Action Strength
 * - Specificity
 * - Impact
 * - Clarity
 * - Achievement Focus
 */
const RadarChart = ({ scores, size = 120, showScore = true }) => {
  const metrics = ['Action', 'Specific', 'Impact', 'Clarity', 'Achievement'];
  const values = [
    scores.actionStrength || 0,
    scores.specificity || 0,
    scores.impact || 0,
    scores.clarity || 0,
    scores.achievementFocus || 0
  ];

  const center = size / 2;
  const maxRadius = size / 2 - 15;
  const angleStep = (Math.PI * 2) / 5;

  // Generate polygon points for score
  const scorePoints = values
    .map((value, i) => {
      const angle = angleStep * i - Math.PI / 2;
      const radius = (value / 3) * maxRadius;
      const x = center + radius * Math.cos(angle);
      const y = center + radius * Math.sin(angle);
      return `${x},${y}`;
    })
    .join(' ');

  // Generate polygon points for max (background)
  const maxPoints = Array(5)
    .fill(0)
    .map((_, i) => {
      const angle = angleStep * i - Math.PI / 2;
      const x = center + maxRadius * Math.cos(angle);
      const y = center + maxRadius * Math.sin(angle);
      return `${x},${y}`;
    })
    .join(' ');

  // Generate grid circles at levels 1, 2, 3
  const gridCircles = [1, 2, 3].map((level) => {
    const points = Array(5)
      .fill(0)
      .map((_, i) => {
        const angle = angleStep * i - Math.PI / 2;
        const radius = (level / 3) * maxRadius;
        const x = center + radius * Math.cos(angle);
        const y = center + radius * Math.sin(angle);
        return `${x},${y}`;
      })
      .join(' ');
    return points;
  });

  // Calculate total score
  const total = values.reduce((a, b) => a + b, 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <svg width={size} height={size} style={{ overflow: 'visible' }}>
        {/* Grid circles */}
        {gridCircles.map((points, i) => (
          <polygon
            key={`grid-${i}`}
            points={points}
            fill="none"
            stroke="rgba(100, 116, 139, 0.15)"
            strokeWidth="1"
          />
        ))}

        {/* Grid lines from center */}
        {Array(5)
          .fill(0)
          .map((_, i) => {
            const angle = angleStep * i - Math.PI / 2;
            const x = center + maxRadius * Math.cos(angle);
            const y = center + maxRadius * Math.sin(angle);
            return (
              <line
                key={`line-${i}`}
                x1={center}
                y1={center}
                x2={x}
                y2={y}
                stroke="rgba(100, 116, 139, 0.15)"
                strokeWidth="1"
              />
            );
          })}

        {/* Max boundary (subtle) */}
        <polygon
          points={maxPoints}
          fill="rgba(59, 130, 246, 0.03)"
          stroke="rgba(59, 130, 246, 0.2)"
          strokeWidth="1"
        />

        {/* Score polygon */}
        <polygon
          points={scorePoints}
          fill="rgba(34, 197, 94, 0.25)"
          stroke="rgba(34, 197, 94, 0.8)"
          strokeWidth="2.5"
          style={{ transition: 'all 0.3s ease' }}
        />

        {/* Data points */}
        {values.map((value, i) => {
          const angle = angleStep * i - Math.PI / 2;
          const radius = (value / 3) * maxRadius;
          const x = center + radius * Math.cos(angle);
          const y = center + radius * Math.sin(angle);
          return (
            <circle
              key={`point-${i}`}
              cx={x}
              cy={y}
              r="4"
              fill="rgba(34, 197, 94, 1)"
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
      </svg>
      {showScore && (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
          {total}/15
        </Typography>
      )}
    </Box>
  );
};

export default RadarChart;
